/**
 * Every region Conistra can route Instagram traffic through.
 *
 * This is the single source of truth for the connect surfaces. It mirrors
 * `_DECODO_ENDPOINTS` in backend/scheduler/instagram_proxy.py exactly — the
 * value is what lands in `workspace.instagram_account_country`, which picks the
 * residential exit node every login and publish for that account travels
 * through. A code that is not in the backend map silently falls back to the US.
 *
 * Parity is enforced by InstagramRegionParityTest (backend/scheduler/tests.py),
 * which reads this file, so adding a region to the backend without adding it
 * here fails the suite rather than quietly hiding it from users.
 *
 * Do not redeclare this list in a component. The two connect surfaces drifted
 * apart by four countries when it lived in two places. See
 * docs/instagram-connection-2026.md.
 */
export type CountryOption = {
  value: string;
  label: string;
  flag: string;
};

export const COUNTRY_OPTIONS: CountryOption[] = [
  { value: 'AR', label: 'Argentina', flag: '🇦🇷' },
  { value: 'AU', label: 'Australia', flag: '🇦🇺' },
  { value: 'AT', label: 'Austria', flag: '🇦🇹' },
  { value: 'AZ', label: 'Azerbaijan', flag: '🇦🇿' },
  { value: 'BD', label: 'Bangladesh', flag: '🇧🇩' },
  { value: 'BE', label: 'Belgium', flag: '🇧🇪' },
  { value: 'BR', label: 'Brazil', flag: '🇧🇷' },
  { value: 'BG', label: 'Bulgaria', flag: '🇧🇬' },
  { value: 'CA', label: 'Canada', flag: '🇨🇦' },
  { value: 'CL', label: 'Chile', flag: '🇨🇱' },
  { value: 'CN', label: 'China', flag: '🇨🇳' },
  { value: 'CO', label: 'Colombia', flag: '🇨🇴' },
  { value: 'HR', label: 'Croatia', flag: '🇭🇷' },
  { value: 'CZ', label: 'Czechia', flag: '🇨🇿' },
  { value: 'DK', label: 'Denmark', flag: '🇩🇰' },
  { value: 'EC', label: 'Ecuador', flag: '🇪🇨' },
  { value: 'EG', label: 'Egypt', flag: '🇪🇬' },
  { value: 'EE', label: 'Estonia', flag: '🇪🇪' },
  { value: 'EU', label: 'Europe (regional)', flag: '🇪🇺' },
  { value: 'FI', label: 'Finland', flag: '🇫🇮' },
  { value: 'FR', label: 'France', flag: '🇫🇷' },
  { value: 'DE', label: 'Germany', flag: '🇩🇪' },
  { value: 'GH', label: 'Ghana', flag: '🇬🇭' },
  { value: 'GR', label: 'Greece', flag: '🇬🇷' },
  { value: 'HK', label: 'Hong Kong', flag: '🇭🇰' },
  { value: 'HU', label: 'Hungary', flag: '🇭🇺' },
  { value: 'IN', label: 'India', flag: '🇮🇳' },
  { value: 'ID', label: 'Indonesia', flag: '🇮🇩' },
  { value: 'IR', label: 'Iran', flag: '🇮🇷' },
  { value: 'IE', label: 'Ireland', flag: '🇮🇪' },
  { value: 'IL', label: 'Israel', flag: '🇮🇱' },
  { value: 'IT', label: 'Italy', flag: '🇮🇹' },
  { value: 'JP', label: 'Japan', flag: '🇯🇵' },
  { value: 'KZ', label: 'Kazakhstan', flag: '🇰🇿' },
  { value: 'KE', label: 'Kenya', flag: '🇰🇪' },
  { value: 'LV', label: 'Latvia', flag: '🇱🇻' },
  { value: 'LT', label: 'Lithuania', flag: '🇱🇹' },
  { value: 'MY', label: 'Malaysia', flag: '🇲🇾' },
  { value: 'MX', label: 'Mexico', flag: '🇲🇽' },
  { value: 'MA', label: 'Morocco', flag: '🇲🇦' },
  { value: 'NL', label: 'Netherlands', flag: '🇳🇱' },
  { value: 'NZ', label: 'New Zealand', flag: '🇳🇿' },
  { value: 'NG', label: 'Nigeria', flag: '🇳🇬' },
  { value: 'NO', label: 'Norway', flag: '🇳🇴' },
  { value: 'PK', label: 'Pakistan', flag: '🇵🇰' },
  { value: 'PE', label: 'Peru', flag: '🇵🇪' },
  { value: 'PH', label: 'Philippines', flag: '🇵🇭' },
  { value: 'PL', label: 'Poland', flag: '🇵🇱' },
  { value: 'PT', label: 'Portugal', flag: '🇵🇹' },
  { value: 'QA', label: 'Qatar', flag: '🇶🇦' },
  { value: 'RO', label: 'Romania', flag: '🇷🇴' },
  { value: 'RU', label: 'Russia', flag: '🇷🇺' },
  { value: 'SA', label: 'Saudi Arabia', flag: '🇸🇦' },
  { value: 'RS', label: 'Serbia', flag: '🇷🇸' },
  { value: 'SG', label: 'Singapore', flag: '🇸🇬' },
  { value: 'SK', label: 'Slovakia', flag: '🇸🇰' },
  { value: 'SI', label: 'Slovenia', flag: '🇸🇮' },
  { value: 'ZA', label: 'South Africa', flag: '🇿🇦' },
  { value: 'KR', label: 'South Korea', flag: '🇰🇷' },
  { value: 'ES', label: 'Spain', flag: '🇪🇸' },
  { value: 'LK', label: 'Sri Lanka', flag: '🇱🇰' },
  { value: 'SE', label: 'Sweden', flag: '🇸🇪' },
  { value: 'CH', label: 'Switzerland', flag: '🇨🇭' },
  { value: 'TW', label: 'Taiwan', flag: '🇹🇼' },
  { value: 'TH', label: 'Thailand', flag: '🇹🇭' },
  { value: 'TR', label: 'Türkiye', flag: '🇹🇷' },
  { value: 'UA', label: 'Ukraine', flag: '🇺🇦' },
  { value: 'AE', label: 'United Arab Emirates', flag: '🇦🇪' },
  { value: 'GB', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'US', label: 'United States', flag: '🇺🇸' },
  { value: 'VN', label: 'Vietnam', flag: '🇻🇳' },
];

/** Look up a region's display data by ISO code (case-insensitive). */
export function findCountry(code: string | null | undefined): CountryOption | undefined {
  if (!code) return undefined;
  const upper = code.trim().toUpperCase();
  return COUNTRY_OPTIONS.find((c) => c.value === upper);
}

/** True when the code is a region the backend can actually route. */
export function isSupportedCountry(code: string | null | undefined): boolean {
  return findCountry(code) !== undefined;
}

/**
 * Best guess at the account's home region, in descending order of confidence:
 * what the workspace already stored, then the user's own country, then the
 * browser's locale region, and only then the US.
 *
 * The US is the last resort rather than the default on purpose: onboarding used
 * to hardcode it, so every user who never opened the dropdown published from a
 * US IP regardless of where they actually were.
 */
export function resolveDefaultCountry(
  workspaceCountry?: string | null,
  userCountry?: string | null
): string {
  const stored = findCountry(workspaceCountry);
  if (stored) return stored.value;

  const fromUser = findCountry(userCountry);
  if (fromUser) return fromUser.value;

  const fromLocale = findCountry(browserRegion());
  if (fromLocale) return fromLocale.value;

  return 'US';
}

/** The region subtag of the browser locale, e.g. "de-CH" -> "CH". */
function browserRegion(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const locale = navigator.language;
  if (!locale) return undefined;
  // Intl gives the most reliable answer where it exists; fall back to parsing.
  try {
    const region = new Intl.Locale(locale).region;
    if (region) return region;
  } catch {
    // Older browsers without Intl.Locale — parse the tag directly.
  }
  const parts = locale.split('-');
  return parts.length > 1 ? parts[parts.length - 1] : undefined;
}
